import { ChangeDetectionStrategy, Component, DestroyRef, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, Subject, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TableModule } from 'primeng/table';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { PessoaFotoDialogComponent } from '@/shared/components/pessoa/pessoa-foto-dialog/pessoa-foto-dialog';
import { EstruturaService } from '@/core/api/sicom-api/sistema/estrutura/estrutura.service';
import { CoordenadorResponse } from '@/core/api/sicom-api/sistema/estrutura/estrutura.model';
import { iniciais } from '@/shared/utils/nome-utils';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-pesquisa-coordenador',
    standalone: true,
    imports: [FormsModule, ButtonModule, InputTextModule, IconFieldModule, InputIconModule, TableModule, PessoaAvatarComponent, PessoaFotoDialogComponent],
    templateUrl: './pesquisa-coordenador.html',
    styleUrl: './pesquisa-coordenador.scss'
})
/** Busca de coordenador (pessoa) para vincular a uma estrutura organizacional. */
export class PesquisaCoordenador {
    private readonly service = inject(EstruturaService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly busca$ = new Subject<string>();

    selecionado = output<CoordenadorResponse>();

    protected termoBusca = '';
    protected readonly resultados = signal<CoordenadorResponse[]>([]);
    protected readonly carregando = signal(false);
    protected readonly buscaRealizada = signal(false);

    constructor() {
        this.busca$
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                switchMap((q) => {
                    this.carregando.set(true);
                    // catchError DENTRO do switchMap: uma falha zera o resultado sem encerrar o stream (buscas seguintes seguem funcionando).
                    return this.service.buscarCoordenador(q, 0, 15).pipe(
                        catchError(() => {
                            this.resultados.set([]);
                            this.carregando.set(false);
                            return EMPTY;
                        })
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((page) => {
                this.resultados.set(page.content);
                this.carregando.set(false);
                this.buscaRealizada.set(true);
            });
    }

    protected onTermoChange(termo: string): void {
        if (termo.trim().length >= 2) {
            this.busca$.next(termo.trim());
        } else {
            this.resultados.set([]);
            this.buscaRealizada.set(false);
        }
    }

    protected selecionar(item: CoordenadorResponse): void {
        this.selecionado.emit(item);
    }

    /** Foto em exibição no diálogo de ampliação; `null` o mantém fechado. */
    protected readonly fotoAmpliada = signal<string | null>(null);
    protected readonly nomeFotoAmpliada = signal('');

    /** Amplia a foto que o avatar emitiu, anunciando de quem ela é. */
    protected ampliarFoto(url: string, nome: string): void {
        this.nomeFotoAmpliada.set(nome ?? '');
        this.fotoAmpliada.set(url);
    }

    protected readonly iniciais = iniciais;
}
